<template>
  <div class="create-page">

    <div class="create-wrap">

      <!-- Step indicator -->
      <div class="step-indicator">
        <div class="step-node">
          <div class="step-circle" :class="selectedType ? 'step-done' : 'step-active'">
            <span v-if="!selectedType">1</span>
            <span v-else>✓</span>
          </div>
          <div class="step-label" :class="selectedType ? 'done' : 'active'">Choose a starting point</div>
        </div>
        <div class="step-connector" :class="{ 'connector-done': selectedType }"></div>
        <div class="step-node">
          <div class="step-circle" :class="selectedType ? 'step-active' : ''">2</div>
          <div class="step-label" :class="{ active: selectedType }">Tell us about your business</div>
        </div>
      </div>

      <!-- ── STEP 1: Type selection ── -->
      <div v-if="!selectedType">
        <div class="create-headline">How would you like to get started?</div>
        <div class="create-sub">We'll take it from here — no code, no templates, just your business.</div>

        <!-- Capacity indicator (AC003) -->
        <div v-if="siteCapacity" class="capacity-bar">
          <div class="capacity-bar-info">
            <span class="capacity-label">Sites used</span>
            <span class="capacity-count" :class="{ 'capacity-full': siteCapacity.available === 0 }">
              {{ siteCapacity.current }} / {{ siteCapacity.limit }}
            </span>
          </div>
          <div class="capacity-track">
            <div
              class="capacity-fill"
              :class="{ 'capacity-fill-full': siteCapacity.available === 0 }"
              :style="{ width: Math.min(100, (siteCapacity.current / siteCapacity.limit) * 100) + '%' }"
            ></div>
          </div>
          <div v-if="siteCapacity.available === 0" class="capacity-limit-msg">
            You've reached your site limit. Delete an existing site to create a new one.
          </div>
        </div>
        <div v-else-if="siteCapacityError" class="capacity-error-msg">{{ siteCapacityError }}</div>

        <div class="type-grid" :class="{ 'type-grid-locked': siteCapacity?.available === 0 }">

          <!-- From brand (featured) -->
          <div class="type-card type-card-featured" :class="{ 'type-card-disabled': siteCapacity?.available === 0 }" @click="siteCapacity?.available !== 0 && selectCreationType('inspiration')">
            <span class="type-card-icon">✦</span>
            <div class="type-card-title">From your brand</div>
            <div class="type-card-desc">Upload your logo, docs, or just tell us about your business — AI builds the rest</div>
            <span class="type-badge badge-accent">Most popular</span>
          </div>

          <!-- Git import -->
          <div class="type-card" :class="{ 'type-card-disabled': siteCapacity?.available === 0 }" @click="siteCapacity?.available !== 0 && selectCreationType('import')">
            <span class="type-card-icon">⬇</span>
            <div class="type-card-title">Connect a code repository</div>
            <div class="type-card-desc">Link an existing code project and we'll deploy and manage it for you</div>
          </div>

          <!-- Migrate / HTML import — shown only when VITE_ENABLE_MIGRATE=true -->
          <template v-if="enableMigrate">
            <div class="type-card" :class="{ 'type-card-disabled': siteCapacity?.available === 0 }" @click="siteCapacity?.available !== 0 && selectCreationType('clone')">
              <span class="type-card-icon">🌐</span>
              <div class="type-card-title">Migrate a live site</div>
              <div class="type-card-desc">Enter any URL — we clone the design, content and structure for you</div>
              <span class="type-badge badge-success">No rebuild needed</span>
            </div>
            <div class="type-card" :class="{ 'type-card-disabled': siteCapacity?.available === 0 }" @click="siteCapacity?.available !== 0 && selectCreationType('htmlImport')">
              <span class="type-card-icon">📁</span>
              <div class="type-card-title">Upload existing files</div>
              <div class="type-card-desc">Drop in your HTML, ZIP or any web files — we modernise and host them</div>
            </div>
          </template>

          <!-- Contact card shown when migrate flag is off -->
          <div v-else class="type-card type-card-contact">
            <span class="type-card-icon">✉️</span>
            <div class="type-card-title">Migrate or import an existing site</div>
            <div class="type-card-desc">Need to migrate a live site or import existing files? Our team will handle it for you.</div>
            <div class="type-card-contact-links">
              <a href="mailto:onboarding@feature1.ai" class="contact-link">onboarding@feature1.ai</a>
              <span style="color:var(--text-muted);font-size:11px;">or</span>
              <a href="https://tawk.to" target="_blank" class="contact-link">Chat with us →</a>
            </div>
          </div>

        </div>
      </div>

      <!-- ── STEP 2: Configuration form ── -->
      <div v-if="selectedType">
        <button class="back-link" type="button" @click="selectedType = null">← Change method</button>

        <form @submit.prevent="createSite">
          <div class="form-card">

            <!-- Site name -->
            <div class="form-group">
              <label class="form-label">What should we call your website?</label>
              <input
                v-model="form.name"
                type="text"
                required
                class="bw-input"
                placeholder="e.g. BeyondWebsite.ai"
              />
            </div>

            <!-- ── Inspiration form ── -->
            <template v-if="selectedType === 'inspiration'">
              <div class="form-group">
                <label class="form-label">Any website you like the look of? <span class="form-optional">(optional)</span></label>
                <input
                  v-model="form.inspirationUrl"
                  type="url"
                  class="bw-input"
                  placeholder="https://example.com"
                />
                <p class="form-helper">We'll use this as design inspiration only</p>
              </div>

              <div class="form-group">
                <label class="form-label">Your company or brand name <span class="form-required">*</span></label>
                <input
                  v-model="form.companyName"
                  type="text"
                  required
                  class="bw-input"
                  placeholder="e.g. BeyondWebsite.ai"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Drop your brand materials here</label>
                <div class="upload-area" @click="$refs.fileInput.click()">
                  <span class="upload-icon">📄</span>
                  <div class="upload-title">Drop your brand materials here</div>
                  <div class="upload-sub">Logo, company overview, product info, team photos — anything that describes your business.<br>We extract everything automatically.</div>
                  <div class="upload-types">PDF, Word, images · Max 50 MB total</div>
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    accept="image/*,.pdf,.txt,.doc,.docx"
                    class="sr-only"
                    @change="handleFileUpload"
                  />
                </div>
                <div v-if="form.attachments.length > 0" class="file-chips">
                  <div v-for="(file, index) in form.attachments" :key="index" class="file-chip">
                    <span>{{ file.type.includes('image') ? '🖼' : '📄' }}</span>
                    <span class="file-chip-name">{{ file.name }}</span>
                    <button type="button" class="file-chip-remove" @click="removeFile(index)">×</button>
                  </div>
                </div>
              </div>
            </template>

            <!-- ── Import form ── -->
            <template v-if="selectedType === 'import'">
              <div class="form-group">
                <label class="form-label">Git Repository URL</label>
                <input
                  v-model="form.repositoryUrl"
                  type="text"
                  required
                  class="bw-input"
                  :class="{ 'bw-input-error': repoUrlError }"
                  placeholder="https://github.com/your-org/your-site.git"
                />
                <p v-if="repoUrlError" class="form-error">{{ repoUrlError }}</p>
                <p v-else class="form-helper">HTTPS or SSH format (git@github.com:…) both accepted</p>
              </div>

              <div v-if="preflight.error" class="alert alert-error">
                {{ preflight.error }} —
                <button type="submit" class="alert-link">retry</button>
              </div>

              <div v-if="preflight.hasDuplicates && !duplicateConfirmed" class="alert alert-warn">
                <p class="alert-title">Existing site(s) found using this repository</p>
                <p class="alert-sub">Select sites to delete before creating your new one, or create a new independent copy.</p>

                <div v-if="deletionErrors.length > 0" class="alert alert-error" style="margin-top:8px;">
                  <p style="font-weight:600;margin-bottom:4px;">Failed to delete:</p>
                  <ul style="list-style:disc;padding-left:16px;">
                    <li v-for="err in deletionErrors" :key="err.id">{{ err.name }}</li>
                  </ul>
                </div>

                <div v-if="showDeleteConfirm" class="confirm-box">
                  <p class="confirm-title">Permanently delete these sites?</p>
                  <ul class="confirm-list">
                    <li v-for="siteId in selectedForDeletion" :key="siteId">
                      {{ preflight.duplicates.find(d => d.id === siteId)?.name }}
                    </li>
                  </ul>
                  <div class="confirm-actions">
                    <button type="button" class="bw-btn-sm btn-danger" :disabled="isDeletingSelected" @click="executeDeleteAndCreate">
                      {{ isDeletingSelected ? 'Deleting…' : 'Confirm Delete & Create' }}
                    </button>
                    <button type="button" class="bw-btn-sm btn-ghost" :disabled="isDeletingSelected" @click="showDeleteConfirm = false">Cancel</button>
                  </div>
                </div>

                <ul v-if="!showDeleteConfirm" class="dup-list">
                  <li v-for="dup in preflight.duplicates" :key="dup.id" class="dup-item">
                    <input type="checkbox" :checked="isSelectedForDeletion(dup.id)" @change="toggleDeletionSelection(dup.id)" class="dup-check" />
                    <span class="dup-name">{{ dup.name }}</span>
                    <span class="dup-status" :class="{
                      'status-live':     dup.status === 'active',
                      'status-creating': dup.status === 'creating',
                      'status-failed':   dup.status === 'failed',
                    }">{{ dup.status }}</span>
                    <a :href="`/sites/${dup.id}/edit`" target="_blank" class="dup-link">Open →</a>
                  </li>
                </ul>

                <div v-if="!showDeleteConfirm" class="dup-actions">
                  <button type="button" class="bw-btn-sm btn-danger" :disabled="selectedForDeletion.length === 0" @click="showDeleteConfirm = true">
                    Delete Selected &amp; Create New
                  </button>
                  <button type="button" class="bw-btn-sm btn-ghost" @click="duplicateConfirmed = true">Create Independent Copy</button>
                  <button type="button" class="bw-btn-sm btn-text" @click="cancelDuplicatePanel">Cancel — edit URL instead</button>
                </div>
              </div>
            </template>

            <!-- ── Clone form ── -->
            <template v-if="selectedType === 'clone'">
              <div class="form-group">
                <label class="form-label">Website URL to clone <span class="form-required">*</span></label>
                <div class="input-row">
                  <input
                    v-model="form.cloneUrl"
                    type="url"
                    required
                    class="bw-input"
                    placeholder="https://example.com"
                    @keydown.enter.prevent="discoverPages"
                  />
                  <button
                    type="button"
                    class="bw-btn-sm btn-accent-sm"
                    :disabled="!form.cloneUrl || cloneDiscovering"
                    @click="discoverPages"
                  >
                    {{ cloneDiscovering ? 'Scanning…' : 'Discover Pages' }}
                  </button>
                </div>
                <p class="form-helper">We'll scan the sitemap and let you pick which pages to clone.</p>
                <p v-if="cloneDiscoverError" class="form-error">{{ cloneDiscoverError }}</p>
              </div>

              <div v-if="clonePages.length > 0" class="clone-picker">
                <div class="clone-picker-header">
                  <span>{{ form.selectedPages.length }} of {{ clonePages.length }} pages selected</span>
                  <div class="clone-picker-actions">
                    <button type="button" class="picker-link" @click="selectAllPages">All</button>
                    <button type="button" class="picker-link" @click="form.selectedPages = []">None</button>
                  </div>
                </div>
                <ul class="clone-page-list">
                  <li
                    v-for="page in clonePages"
                    :key="page.url"
                    class="clone-page-item"
                    @click="toggleClonePage(page)"
                  >
                    <input
                      type="checkbox"
                      :checked="isClonePageSelected(page)"
                      @click.stop
                      @change="toggleClonePage(page)"
                      class="clone-check"
                    />
                    <div class="clone-page-info">
                      <p class="clone-page-title">{{ page.title || page.url }}</p>
                      <p class="clone-page-url">{{ page.url }}</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div v-if="clonePages.length === 0 && !cloneDiscovering" class="clone-empty">
                Enter a URL above and click <strong>Discover Pages</strong> to see available pages
              </div>
            </template>

            <!-- ── HTML import form ── -->
            <template v-if="selectedType === 'htmlImport'">
              <div class="form-group">
                <label class="form-label">Upload HTML or ZIP <span class="form-required">*</span></label>
                <div class="upload-area" @click="$refs.htmlFileInput.click()">
                  <span class="upload-icon">📁</span>
                  <div class="upload-title">Select or drop your file here</div>
                  <div class="upload-sub">A single <code>.html</code>, <code>.htm</code>, or <code>.zip</code> file containing your website</div>
                  <div class="upload-types">Max 50 MB · ZIP must contain at least one .html file</div>
                  <input
                    ref="htmlFileInput"
                    type="file"
                    accept=".html,.htm,.zip,text/html,application/zip,application/x-zip-compressed"
                    class="sr-only"
                    @change="handleHtmlFileSelect"
                  />
                </div>
                <p v-if="htmlFileError" class="form-error">{{ htmlFileError }}</p>
                <div v-if="form.htmlFile" class="file-chips">
                  <div class="file-chip">
                    <span>📄</span>
                    <span class="file-chip-name">{{ form.htmlFile.name }}</span>
                    <span class="file-chip-size">{{ formatFileSize(form.htmlFile.size) }}</span>
                  </div>
                </div>
              </div>
            </template>

          </div><!-- /form-card -->

          <!-- Consent -->
          <label class="consent-row">
            <input
              v-model="form.antiPhishingConsent"
              type="checkbox"
              required
              class="consent-check"
            />
            <span class="consent-text">I have the rights to create this website and won't use it for deceptive, phishing, or impersonation purposes.</span>
          </label>

          <!-- Server error -->
          <div v-if="submitError" class="alert alert-error" style="margin-bottom:16px;">{{ submitError }}</div>

          <!-- Submit -->
          <button
            type="submit"
            class="start-btn"
            :disabled="loading || !isFormValid"
          >
            <span v-if="loading" class="start-spinner"></span>
            <span>{{ loading ? 'Creating your website…' : '✦ Start building →' }}</span>
          </button>
          <p class="start-sub">Your site will be live in about 5 minutes ⚡</p>

        </form>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useSitesStore } from '../stores/sites'
import { track } from '../plugins/amplitude'

export default {
  name: 'Create',
  components: {},
  setup() {
    const router = useRouter()
    const siteStore = useSitesStore()

    const { siteCapacity, siteCapacityLoading, siteCapacityError } = storeToRefs(siteStore)
    const { fetchSiteCapacity } = siteStore

    onMounted(() => { fetchSiteCapacity() })

    const selectedType = ref(null)
    const loading = ref(false)

    const form = reactive({
      name: '',
      repositoryUrl: '',
      // Enhanced inspiration form fields
      inspirationUrl: '',
      companyName: '',
      attachments: [],
      antiPhishingConsent: false,
      // HTML/ZIP import
      htmlFile: null,
      // Clone
      cloneUrl: '',
      selectedPages: [],
    })

    // Clone-specific state
    const clonePages = ref([])
    const cloneDiscovering = ref(false)
    const cloneDiscoverError = ref('')

    const selectCreationType = (type) => {
      selectedType.value = type
      // Clear mode-specific transient state to avoid cross-mode leakage
      form.htmlFile = null
      htmlFileError.value = ''
      clonePages.value = []
      cloneDiscoverError.value = ''
      form.cloneUrl = ''
      form.selectedPages = []
      preflight.hasDuplicates = false
      preflight.duplicates = []
      preflight.error = null
      duplicateConfirmed.value = false
      selectedForDeletion.value = []
      showDeleteConfirm.value = false
      deletionErrors.value = []
      submitError.value = ''
    }

    const htmlFileError = ref('')

    const validateHtmlFile = (file) => {
      if (!file) return ''
      const name = file.name.toLowerCase()
      const allowedExts = ['.html', '.htm', '.zip']
      const allowedMimes = ['text/html', 'application/zip', 'application/x-zip-compressed']
      const hasValidExt = allowedExts.some(ext => name.endsWith(ext))
      const hasValidMime = !file.type || allowedMimes.includes(file.type)
      if (!hasValidExt || !hasValidMime) {
        return 'Only .html, .htm, or .zip files are supported. Please select a valid file.'
      }
      return ''
    }

    const handleHtmlFileSelect = (event) => {
      const file = event.target.files[0] || null
      const err = validateHtmlFile(file)
      if (err) {
        htmlFileError.value = err
        form.htmlFile = null
        event.target.value = ''
      } else {
        htmlFileError.value = ''
        form.htmlFile = file
      }
    }

    const handleFileUpload = (event) => {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        // Convert file to base64 for storage
        const reader = new FileReader()
        reader.onload = (e) => {
          form.attachments.push({
            name: file.name,
            size: file.size,
            type: file.type,
            data: e.target.result
          })
        }
        reader.readAsDataURL(file)
      })
    }

    const removeFile = (index) => {
      form.attachments.splice(index, 1)
    }

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    // Preflight state for repo import duplicate detection
    const preflight = reactive({ hasDuplicates: false, duplicates: [], error: null })
    const duplicateConfirmed = ref(false)
    const submitError = ref('')
    const repoUrlError = ref('')

    // Delete-selected state
    const selectedForDeletion = ref([])
    const showDeleteConfirm = ref(false)
    const isDeletingSelected = ref(false)
    const deletionErrors = ref([])

    const isSelectedForDeletion = (siteId) => selectedForDeletion.value.includes(siteId)

    const toggleDeletionSelection = (siteId) => {
      const idx = selectedForDeletion.value.indexOf(siteId)
      if (idx >= 0) selectedForDeletion.value.splice(idx, 1)
      else selectedForDeletion.value.push(siteId)
    }

    const executeDeleteAndCreate = async () => {
      isDeletingSelected.value = true
      deletionErrors.value = []

      const toDelete = [...selectedForDeletion.value]
      const failed = []

      for (const siteId of toDelete) {
        try {
          await siteStore.deleteSite(siteId)
          const idx = preflight.duplicates.findIndex(d => d.id === siteId)
          if (idx >= 0) preflight.duplicates.splice(idx, 1)
        } catch (_) {
          const site = preflight.duplicates.find(d => d.id === siteId)
          failed.push({ id: siteId, name: site?.name || siteId })
        }
      }

      selectedForDeletion.value = []
      isDeletingSelected.value = false
      showDeleteConfirm.value = false

      if (failed.length > 0) {
        deletionErrors.value = failed
        if (preflight.duplicates.length === 0) preflight.hasDuplicates = false
        return
      }

      // All selected deleted — update panel state and re-trigger creation
      if (preflight.duplicates.length === 0) {
        preflight.hasDuplicates = false
        preflight.error = null
      }
      await createSite()
    }

    // Validate a repository URL client-side (mirrors backend custom validator).
    // Returns an error string or empty string when valid.
    const validateRepoUrl = (url) => {
      if (!url || !url.trim()) return 'Repository URL is required'
      if (/^git@[^:]+:.+/.test(url)) return ''
      try { new URL(url); return '' } catch (_) {}
      return 'Enter a valid HTTPS (https://…) or SSH (git@…) repository URL'
    }

    // Reset preflight + deletion state when the repo URL changes
    watch(() => form.repositoryUrl, (val) => {
      preflight.hasDuplicates = false
      preflight.duplicates = []
      preflight.error = null
      duplicateConfirmed.value = false
      selectedForDeletion.value = []
      showDeleteConfirm.value = false
      deletionErrors.value = []
      repoUrlError.value = val ? validateRepoUrl(val) : ''
    })

    // Cancel from the duplicate panel — clears the panel and keeps the form intact.
    // Does not call any import creation endpoint.
    const cancelDuplicatePanel = () => {
      preflight.hasDuplicates = false
      preflight.duplicates = []
      preflight.error = null
      duplicateConfirmed.value = false
      selectedForDeletion.value = []
      showDeleteConfirm.value = false
      deletionErrors.value = []
      form.repositoryUrl = ''
    }

    const discoverPages = async () => {
      if (!form.cloneUrl || cloneDiscovering.value) return
      cloneDiscovering.value = true
      cloneDiscoverError.value = ''
      clonePages.value = []
      form.selectedPages = []
      try {
        let url = form.cloneUrl.trim()
        if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url
        form.cloneUrl = url
        const result = await siteStore.discoverClonePages(url)
        clonePages.value = result.pages || []
        // Pre-select top 3 pages
        form.selectedPages = clonePages.value.slice(0, 3).map(p => ({ url: p.url, title: p.title }))
      } catch (err) {
        cloneDiscoverError.value = err.response?.data?.error || err.message || 'Could not discover pages. Check the URL and try again.'
      } finally {
        cloneDiscovering.value = false
      }
    }

    const isClonePageSelected = (page) => form.selectedPages.some(p => p.url === page.url)

    const toggleClonePage = (page) => {
      const idx = form.selectedPages.findIndex(p => p.url === page.url)
      if (idx >= 0) form.selectedPages.splice(idx, 1)
      else form.selectedPages.push({ url: page.url, title: page.title })
    }

    const selectAllPages = () => {
      form.selectedPages = clonePages.value.map(p => ({ url: p.url, title: p.title }))
    }

    const isFormValid = computed(() => {
      const baseValid = form.name && form.antiPhishingConsent

      if (selectedType.value === 'inspiration') {
        return baseValid && form.companyName && form.attachments.length > 0
      }

      if (selectedType.value === 'import') {
        const duplicateOk = !preflight.hasDuplicates || duplicateConfirmed.value
        const urlValid = form.repositoryUrl && !repoUrlError.value
        return baseValid && urlValid && duplicateOk
      }

      if (selectedType.value === 'htmlImport') {
        return baseValid && !!form.htmlFile && !htmlFileError.value
      }

      if (selectedType.value === 'clone') {
        return baseValid && !!form.cloneUrl && form.selectedPages.length > 0
      }

      return false
    })

    const createSite = async () => {
      // Double-click guard: ignore if already in-flight
      if (loading.value) return
      // Required fields guard — prevents programmatic submission when form is incomplete
      if (!isFormValid.value) return
      loading.value = true
      submitError.value = ''

      try {
        const siteData = { name: form.name }

        if (selectedType.value === 'inspiration') {
          let url = form.inspirationUrl.trim()
          if (url && !/^https?:\/\//i.test(url)) {
            url = 'https://' + url
          }
          siteData.inspirationUrl = url || undefined
          siteData.brandData = { companyName: form.companyName, attachments: form.attachments }
        }

        if (selectedType.value === 'import') {
          // Guard: validate URL format before hitting the network — no preflight on invalid input
          const urlErr = validateRepoUrl(form.repositoryUrl)
          if (urlErr) {
            repoUrlError.value = urlErr
            return
          }

          // Run preflight before import — any failure blocks creation and shows a retry prompt
          let preflightResult
          try {
            preflightResult = await siteStore.preflightRepoImport(form.repositoryUrl)
            preflight.error = null
          } catch (preflightErr) {
            preflight.error = preflightErr.message || 'Duplicate check failed. Please retry.'
            return // Block import — user sees error and can retry by clicking Create Website again
          }

          if (preflightResult.hasDuplicates && !duplicateConfirmed.value) {
            preflight.hasDuplicates = true
            preflight.duplicates = preflightResult.duplicates
            return // Show duplicate panel; user must confirm before proceeding
          }
          siteData.repositoryUrl = form.repositoryUrl
          if (duplicateConfirmed.value) siteData.independentCopy = true
        }

        if (selectedType.value === 'clone') {
          siteData.cloneUrl = form.cloneUrl
          siteData.selectedPages = form.selectedPages
        }

        let result
        if (selectedType.value === 'htmlImport') {
          result = await siteStore.createHtmlImportSite(siteData, form.htmlFile)
        } else if (selectedType.value === 'clone') {
          result = await siteStore.createSite(siteData, 'clone')
        } else {
          result = await siteStore.createSite(siteData, selectedType.value)
        }
        const newSiteId = result?.site?.id
        if (!newSiteId) {
          submitError.value = 'Site was created but no ID was returned. Please check your sites list.'
          return
        }
        track('site_creation_started', { siteId: newSiteId, siteName: form.name, creationType: selectedType.value })
        fetchSiteCapacity()
        router.push(`/sites/${newSiteId}/edit`)
      } catch (error) {
        let msg
        if (error.response?.status === 429) {
          msg = error.response?.data?.message
            || `You've reached your site limit. Delete an existing site to create a new one.`
          fetchSiteCapacity()
        } else if (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('network'))) {
          msg = 'Network error — check your connection and try again.'
        } else if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
          msg = 'Request timed out — please try again.'
        } else {
          msg = error.response?.data?.error
            || error.response?.data?.message
            || error.response?.data?.validationErrors?.[0]?.msg
            || error.response?.data?.details?.validationErrors?.[0]?.msg
            || error.message
            || 'Something went wrong. Please try again.'
        }
        submitError.value = msg
      } finally {
        loading.value = false
      }
    }

    const enableMigrate = import.meta.env.VITE_ENABLE_MIGRATE === 'true'

    return {
      enableMigrate,
      selectedType,
      loading,
      submitError,
      siteCapacity,
      siteCapacityLoading,
      siteCapacityError,
      repoUrlError,
      htmlFileError,
      preflight,
      duplicateConfirmed,
      cancelDuplicatePanel,
      form,
      selectCreationType,
      createSite,
      handleFileUpload,
      handleHtmlFileSelect,
      removeFile,
      formatFileSize,
      isFormValid,
      // Clone
      clonePages,
      cloneDiscovering,
      cloneDiscoverError,
      discoverPages,
      isClonePageSelected,
      toggleClonePage,
      selectAllPages,
      selectedForDeletion,
      showDeleteConfirm,
      isDeletingSelected,
      deletionErrors,
      isSelectedForDeletion,
      toggleDeletionSelection,
      executeDeleteAndCreate,
    }
  }
}
</script>

<style scoped>
.create-page { min-height: 100vh; }

.create-wrap { max-width: 720px; margin: 0 auto; padding: 48px 24px 80px; }

/* ── Step indicator ── */
.step-indicator {
  display: flex; align-items: center; gap: 0;
  margin-bottom: 44px;
}
.step-node {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  flex: 1; position: relative;
}
.step-circle {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; transition: all 0.25s;
  border: 2px solid rgba(255,255,255,0.15);
  background: var(--glass-bg);
  color: var(--text-muted);
  position: relative; z-index: 1;
}
.step-circle.step-active {
  background: var(--accent); border-color: var(--accent);
  color: #fff; box-shadow: 0 0 16px var(--accent-glow);
}
.step-circle.step-done {
  background: rgba(52,211,153,0.15); border-color: var(--success);
  color: var(--success);
}
.step-label { font-size: 11px; font-weight: 500; color: var(--text-muted); white-space: nowrap; text-align: center; }
.step-label.active { color: var(--text-primary); }
.step-label.done { color: var(--success); }
.step-connector {
  flex: 1; height: 1px; background: rgba(255,255,255,0.1);
  align-self: flex-start; margin-top: 16px; max-width: 200px;
}
.step-connector.connector-done { background: rgba(52,211,153,0.4); }

/* ── Type selection ── */
.create-headline { font-size: 24px; font-weight: 800; color: var(--text-primary); margin-bottom: 8px; }
.create-sub { font-size: 14px; color: var(--text-secondary); margin-bottom: 28px; line-height: 1.5; }

/* ── Capacity indicator ── */
.capacity-bar {
  background: var(--glass-bg);
  border: var(--glass-border);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  margin-bottom: 20px;
}
.capacity-bar-info { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.capacity-label { font-size: 12px; color: var(--text-muted); font-weight: 500; }
.capacity-count { font-size: 12px; font-weight: 700; color: var(--text-secondary); }
.capacity-count.capacity-full { color: #f87171; }
.capacity-track {
  height: 4px; border-radius: 2px;
  background: rgba(255,255,255,0.08);
  overflow: hidden;
}
.capacity-fill {
  height: 100%; border-radius: 2px;
  background: var(--accent);
  transition: width 0.4s ease;
}
.capacity-fill.capacity-fill-full { background: #f87171; }
.capacity-limit-msg {
  margin-top: 10px; font-size: 12px; color: #f87171;
  font-weight: 500;
}
.capacity-error-msg {
  font-size: 12px; color: var(--text-muted);
  margin-bottom: 16px;
}
.type-card-disabled {
  opacity: 0.4; cursor: not-allowed; pointer-events: none;
}

.type-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px;
  margin-bottom: 32px;
}
@media (max-width: 540px) { .type-grid { grid-template-columns: 1fr; } }

.type-card {
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  border: var(--glass-border);
  border-radius: var(--radius-lg);
  padding: 22px 20px;
  cursor: pointer; transition: all 0.2s;
}
.type-card:hover {
  background: var(--glass-bg-hover); border: var(--glass-border-hi);
  box-shadow: var(--elev-md); transform: translateY(-2px);
}
.type-card-featured {
  background: linear-gradient(135deg, rgba(124,109,250,0.14), rgba(167,139,250,0.08));
  border: 1px solid rgba(124,109,250,0.3);
}
.type-card-featured:hover {
  background: linear-gradient(135deg, rgba(124,109,250,0.2), rgba(167,139,250,0.12));
  border-color: rgba(124,109,250,0.5);
}
.type-card-icon { font-size: 26px; margin-bottom: 10px; display: block; }
.type-card-title { font-size: 15px; font-weight: 700; color: var(--text-primary); margin-bottom: 6px; }
.type-card-desc { font-size: 13px; color: var(--text-secondary); line-height: 1.5; }
.type-card-contact { cursor: default; }
.type-card-contact:hover { transform: none; border-color: rgba(255,255,255,0.08); }
.type-card-contact-links { display: flex; align-items: center; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
.contact-link { font-size: 12px; color: var(--accent); text-decoration: none; font-weight: 500; }
.contact-link:hover { text-decoration: underline; }
.type-badge {
  display: inline-block; margin-top: 10px;
  padding: 3px 9px; border-radius: 20px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.05em;
}
.badge-accent { background: var(--accent-soft); border: 1px solid rgba(124,109,250,0.3); color: var(--accent); }
.badge-success { background: rgba(52,211,153,0.1); border: 1px solid rgba(52,211,153,0.25); color: var(--success); }

/* ── Form ── */
.back-link {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 500; color: var(--text-muted);
  cursor: pointer; margin-bottom: 28px; transition: color 0.15s;
  background: none; border: none; font-family: inherit; padding: 0;
}
.back-link:hover { color: var(--text-primary); }

.form-card {
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  border: var(--glass-border);
  border-radius: var(--radius-lg);
  padding: 24px;
  box-shadow: var(--elev-sm);
  margin-bottom: 20px;
}
.form-group { margin-bottom: 20px; }
.form-group:last-child { margin-bottom: 0; }
.form-label { display: block; font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }
.form-optional { font-size: 11px; color: var(--text-muted); font-weight: 400; }
.form-required { color: var(--accent); }
.form-helper { font-size: 11px; color: var(--text-muted); margin-top: 4px; }
.form-error { font-size: 11px; color: var(--danger); margin-top: 4px; }

.bw-input {
  width: 100%; padding: 10px 13px;
  background: var(--glass-bg-active);
  border: var(--glass-border);
  border-radius: var(--radius-sm);
  color: var(--text-primary); font-size: 14px;
  outline: none; transition: all 0.15s; font-family: inherit;
}
.bw-input::placeholder { color: var(--text-muted); }
.bw-input:focus { border: var(--glass-border-hi); background: rgba(255,255,255,0.10); box-shadow: 0 0 0 3px rgba(124,109,250,0.12); }
.bw-input-error { border-color: rgba(248,113,113,0.5); }
.bw-input-error:focus { box-shadow: 0 0 0 3px rgba(248,113,113,0.12); }

.input-row { display: flex; gap: 8px; }
.input-row .bw-input { flex: 1; }

/* Upload area */
.upload-area {
  width: 100%;
  border: 1px dashed rgba(255,255,255,0.22);
  border-radius: var(--radius-md);
  padding: 24px 20px; text-align: center; cursor: pointer;
  transition: all 0.2s; background: rgba(255,255,255,0.02);
}
.upload-area:hover { border-color: rgba(124,109,250,0.5); background: rgba(124,109,250,0.05); }
.upload-icon { font-size: 28px; margin-bottom: 8px; display: block; }
.upload-title { font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px; }
.upload-sub { font-size: 12px; color: var(--text-secondary); line-height: 1.5; margin-bottom: 4px; }
.upload-types { font-size: 11px; color: var(--text-muted); }

/* File chips */
.file-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.file-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px; border-radius: var(--radius-sm);
  background: rgba(124,109,250,0.12); border: 1px solid rgba(124,109,250,0.25);
  font-size: 12px; font-weight: 500; color: var(--text-primary);
}
.file-chip-name { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-chip-size { color: var(--text-muted); font-size: 11px; }
.file-chip-remove { color: var(--text-muted); cursor: pointer; background: none; border: none; font-size: 14px; line-height: 1; padding: 0; }
.file-chip-remove:hover { color: var(--danger); }

/* Alert boxes */
.alert {
  padding: 14px 16px; border-radius: var(--radius-sm);
  font-size: 13px; margin-bottom: 16px;
}
.alert-error { background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.25); color: var(--danger); }
.alert-warn  { background: rgba(251,191,36,0.08); border: 1px solid rgba(251,191,36,0.2); color: var(--warning); }
.alert-title { font-weight: 600; margin-bottom: 4px; }
.alert-sub   { font-size: 12px; color: var(--text-secondary); }
.alert-link  { text-decoration: underline; cursor: pointer; background: none; border: none; color: inherit; font: inherit; }

/* Dup list */
.dup-list { list-style: none; padding: 0; margin: 10px 0; display: flex; flex-direction: column; gap: 6px; }
.dup-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; border-radius: var(--radius-sm);
  background: var(--glass-bg); border: var(--glass-border);
  font-size: 12px;
}
.dup-check { width: 14px; height: 14px; cursor: pointer; flex-shrink: 0; }
.dup-name { flex: 1; font-weight: 500; color: var(--text-primary); }
.dup-status { padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; text-transform: capitalize; }
.dup-link { color: var(--accent); font-size: 11px; text-decoration: none; }
.dup-link:hover { text-decoration: underline; }
.dup-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }

/* Confirm box */
.confirm-box {
  background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.2);
  border-radius: var(--radius-sm); padding: 12px 14px; margin-top: 10px;
}
.confirm-title { font-size: 13px; font-weight: 600; color: var(--danger); margin-bottom: 8px; }
.confirm-list { list-style: disc; padding-left: 18px; font-size: 12px; color: var(--danger); margin-bottom: 8px; }
.confirm-actions { display: flex; gap: 8px; }

/* Small buttons */
.bw-btn-sm {
  display: inline-flex; align-items: center;
  padding: 7px 14px; border-radius: var(--radius-sm);
  font-size: 12px; font-weight: 600; cursor: pointer;
  border: none; transition: all 0.15s; font-family: inherit; white-space: nowrap;
}
.btn-accent-sm { background: var(--accent); color: #fff; box-shadow: 0 0 12px var(--accent-glow); }
.btn-accent-sm:hover { background: #6d5ef0; }
.btn-accent-sm:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-danger { background: rgba(248,113,113,0.15); color: var(--danger); border: 1px solid rgba(248,113,113,0.3); }
.btn-danger:hover { background: rgba(248,113,113,0.25); }
.btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-ghost { background: var(--glass-bg); color: var(--text-secondary); border: var(--glass-border); }
.btn-ghost:hover { background: var(--glass-bg-hover); color: var(--text-primary); }
.btn-text { background: transparent; color: var(--text-muted); border: none; cursor: pointer; }
.btn-text:hover { color: var(--text-secondary); }

/* Clone picker */
.clone-picker {
  border: var(--glass-border); border-radius: var(--radius-md);
  overflow: hidden; margin-top: 8px;
}
.clone-picker-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px;
  background: var(--glass-bg-active);
  font-size: 12px; font-weight: 600; color: var(--text-secondary);
}
.clone-picker-actions { display: flex; gap: 12px; }
.picker-link { background: none; border: none; font-size: 11px; color: var(--accent); cursor: pointer; font-family: inherit; }
.picker-link:hover { text-decoration: underline; }
.clone-page-list { max-height: 220px; overflow-y: auto; list-style: none; padding: 0; }
.clone-page-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 14px; cursor: pointer; transition: background 0.12s;
  border-top: var(--glass-border);
}
.clone-page-item:hover { background: var(--glass-bg-hover); }
.clone-check { width: 14px; height: 14px; margin-top: 2px; flex-shrink: 0; cursor: pointer; }
.clone-page-info { flex: 1; min-width: 0; }
.clone-page-title { font-size: 12px; font-weight: 500; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.clone-page-url   { font-size: 10px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.clone-empty {
  text-align: center; padding: 24px;
  font-size: 12px; color: var(--text-muted);
  border: 1px dashed rgba(255,255,255,0.12); border-radius: var(--radius-md);
  margin-top: 8px;
}

/* ── Consent ── */
.consent-row {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 14px 16px; cursor: pointer;
  background: rgba(52,211,153,0.05);
  border: 1px solid rgba(52,211,153,0.15);
  border-radius: var(--radius-sm);
  margin-bottom: 20px;
}
.consent-row:hover { background: rgba(52,211,153,0.08); }
.consent-check { width: 15px; height: 15px; margin-top: 2px; flex-shrink: 0; cursor: pointer; accent-color: var(--success); }
.consent-text { font-size: 12px; color: var(--text-secondary); line-height: 1.5; }

/* ── Start button ── */
.start-btn {
  width: 100%; padding: 15px;
  background: var(--accent); color: #fff;
  border: none; border-radius: var(--radius-md);
  font-size: 16px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  box-shadow: 0 0 32px var(--accent-glow);
  transition: all 0.2s; font-family: inherit;
}
.start-btn:hover:not(:disabled) { background: #6d5ef0; box-shadow: 0 0 48px var(--accent-glow); transform: translateY(-1px); }
.start-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.start-spinner {
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff; border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.start-sub { text-align: center; margin-top: 10px; font-size: 12px; color: var(--text-muted); }

.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; }

@media (max-width: 640px) {
  .create-wrap { padding: 24px 16px 60px; }
  .form-card { padding: 16px; }
}
</style>
